import { client } from '../sanity'
import type { Bundle } from '@/types/bundle'

/**
 * Stock Bundle type - represents a stockBundle document
 * that references a stock item in its stockOrder.stockBundleStockItems array
 */
export interface StockBundleUsage {
  _id: string
  _type: 'stockBundle'
  title: string
  sku: string
  quantity: number // Quantity of the stock item used in this bundle
}

/**
 * Stock Bundle type for list display
 */
export interface StockBundle {
  _id: string
  _type: 'stockBundle'
  title: string
  sku: string
  selling: {
    sellAt: number
  }
  stockOrder: {
    stockBundleStockItems: Array<{
      stockItem: {
        _ref: string
      }
      quantity: number
    }>
    availableStock?: number
  }
  zohoMapping?: {
    zohoSyncStatus: 'not_synced' | 'synced' | 'error'
    zohoBundleId?: string
    zohoItemId?: string
  }
  _createdAt?: string
  _updatedAt?: string
}

/**
 * Fetch all bundles
 */
export const getAllBundles = async (): Promise<Bundle[]> => {
  return client.fetch(
    `*[_type == "bundle"] | order(_updatedAt desc) {
      _id,
      _type,
      sku,
      name,
      description,
      status,
      zohoItemId,
      components,
      currentStock,
      reorderPoint,
      lastStockSync,
      _createdAt,
      _updatedAt
    }`
  )
}

/**
 * Stock Bundle Detail type - full bundle with populated stock items
 */
export interface StockBundleDetail extends StockBundle {
  stockOrder: {
    stockBundleStockItems: Array<{
      stockItem: {
        _ref: string
        name?: string
        sku?: string
        category?: {
          _ref: string
          name?: string
          code?: string
        }
      }
      quantity: number
      price?: number
    }>
    availableStock?: number
    reorderPoint?: number
  }
  zohoMapping?: {
    zohoSyncStatus: 'not_synced' | 'synced' | 'error'
    zohoBundleId?: string
    zohoItemId?: string
    lastSync?: string
  }
  weight?: number
  assemblyTime?: number
}

/**
 * Fetch all stock bundles for list display
 */
export const getAllStockBundles = async (): Promise<StockBundle[]> => {
  return client.fetch(
    `*[_type == "stockBundle"] | order(_updatedAt desc) {
      _id,
      _type,
      title,
      sku,
      selling {
        sellAt
      },
      stockOrder {
        stockBundleStockItems[] {
          stockItem {
            _ref
          },
          quantity
        },
        availableStock
      },
      zohoMapping {
        zohoSyncStatus,
        zohoBundleId,
        zohoItemId
      },
      _createdAt,
      _updatedAt
    }`
  )
}

/**
 * Fetch a single stock bundle by ID with populated stock items
 */
export const getStockBundleById = async (id: string): Promise<StockBundleDetail | null> => {
  return client.fetch(
    `*[_type == "stockBundle" && _id == $id][0] {
      _id,
      _type,
      title,
      sku,
      selling {
        sellAt
      },
      stockOrder {
        stockBundleStockItems[] {
          stockItem-> {
            _id,
            _ref,
            name,
            sku,
            category-> {
              _id,
              _ref,
              name,
              code
            }
          },
          quantity,
          price
        },
        availableStock,
        reorderPoint
      },
      zohoMapping {
        zohoSyncStatus,
        zohoBundleId,
        zohoItemId,
        lastSync
      },
      weight,
      assemblyTime,
      _createdAt,
      _updatedAt
    }`,
    { id }
  )
}

/**
 * Fetch a single bundle by SKU
 */
export const getBundleBySku = async (sku: string): Promise<Bundle | null> => {
  return client.fetch(
    `*[_type == "bundle" && sku == $sku][0] {
      _id,
      _type,
      sku,
      name,
      description,
      status,
      zohoItemId,
      components,
      currentStock,
      reorderPoint,
      lastStockSync,
      _createdAt,
      _updatedAt
    }`,
    { sku }
  )
}

/**
 * Fetch stock bundles that use a specific stock item
 * Query filters stockBundle documents where the stockOrder.stockBundleStockItems array
 * contains a reference to the specified stock item
 */
export const getStockBundlesByStockItemId = async (
  stockItemId: string
): Promise<StockBundleUsage[]> => {
  if (!stockItemId) {
    return []
  }

  try {
    // Query all stockBundles and filter in JavaScript to handle edge cases
    const bundles = await client.fetch(
      `*[_type == "stockBundle" && defined(stockOrder.stockBundleStockItems)] {
        _id,
        _type,
        title,
        sku,
        stockOrder {
          stockBundleStockItems[] {
            stockItem {
              _ref
            },
            quantity
          }
        }
      } | order(title asc)`,
      {}
    )

    // Filter bundles that contain this stock item
    const result = bundles
      .filter((bundle: any) => {
        // Check if bundle has stockOrder and stockBundleStockItems
        if (!bundle.stockOrder || !bundle.stockOrder.stockBundleStockItems || !Array.isArray(bundle.stockOrder.stockBundleStockItems)) {
          return false
        }
        
        // Check if any item in the array matches our stock item ID
        return bundle.stockOrder.stockBundleStockItems.some(
          (item: any) => {
            return item && item.stockItem && item.stockItem._ref === stockItemId
          }
        )
      })
      .map((bundle: any) => {
        // Find the matching stock item in the array
        const matchedItem = bundle.stockOrder.stockBundleStockItems.find(
          (item: any) => {
            return item && item.stockItem && item.stockItem._ref === stockItemId
          }
        )

        if (!matchedItem) {
          return null
        }

        return {
          _id: bundle._id,
          _type: bundle._type,
          title: bundle.title || 'Untitled Bundle',
          sku: bundle.sku || 'No SKU',
          quantity: matchedItem.quantity || 0
        }
      })
      .filter((bundle: any) => bundle !== null) as StockBundleUsage[]

    // Debug logging in development
    if (process.env.NODE_ENV === 'development') {
      console.log('getStockBundlesByStockItemId:', {
        stockItemId,
        bundlesFound: bundles.length,
        resultCount: result.length,
        result,
        sampleBundle: bundles[0] ? {
          _id: bundles[0]._id,
          title: bundles[0].title,
          sku: bundles[0].sku,
          stockItems: bundles[0].stockOrder?.stockBundleStockItems?.map((item: any) => ({
            stockItemRef: item.stockItem?._ref,
            quantity: item.quantity
          }))
        } : null
      })
    }

    return result
  } catch (error) {
    console.error('Error fetching bundles by stock item ID:', error)
    throw error
  }
}
